<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { imageStore, editStore, sessionStore } from '../stores';
  import ResultTools from '../components/ResultTools.svelte';
  import ImagePreview from '../components/ImagePreview.svelte';
  import UploadSection from '../components/UploadSection.svelte';
  import { selectResult, saveTasksToServer } from '../utils/apiClient';
  import { compressImage, fileToBase64, downloadFile } from '../utils/imageUtils';

  let fileInput;
  let _objectUrl;
  let _imageSelectionVersion = 0;
  let errorMessage = '';
  let patternText = 'E-RION';

  // 다중 결과 이미지 네비게이션
  let selectedImageIndex = 0;
  let _lastResultImagesRef = null; // 실제 이미지 목록이 바뀔 때만 인덱스 리셋
  $: resultImages = $imageStore.resultImages;
  $: hasMultipleResults = resultImages && resultImages.length >= 2;
  // resultImages 배열 참조가 바뀔 때만 인덱스 초기화 (resultImage 변경은 무시)
  $: {
    if (resultImages !== _lastResultImagesRef) {
      _lastResultImagesRef = resultImages;
      selectedImageIndex = 0;
    }
  }

  function selectResultImage(index) {
    if (!resultImages || index < 0 || index >= resultImages.length) return;
    selectedImageIndex = index;
    // 스토어의 resultImages는 그대로 두고 resultImage만 교체
    const target = resultImages[index];
    imageStore.update(s => ({ ...s, resultImage: target }));
  }

  // 갤러리 저장 선택 상태
  $: resultTask = { ...($editStore.dashboardTasks.find(task => task.id === $editStore.currentTaskId) || {}),
    featureId: $imageStore.resultFeature || $editStore.dashboardTasks.find(task => task.id === $editStore.currentTaskId)?.featureId,
    options: $imageStore.resultOptions || {} };
  let gallerySelectionDone = false;
  let selecting = false;
  $: if (!hasMultipleResults) { gallerySelectionDone = false; }

  async function selectForGallery() {
    if (!resultImages || gallerySelectionDone || selecting) return;
    selecting = true;
    const selectedUrl = resultImages[selectedImageIndex];
    const session = get(sessionStore);
    // 현재 작업의 process_id 가져오기
    const store = get(editStore);
    const task = store.dashboardTasks.find(t => t.id === store.currentTaskId);
    const processId = task?.process_id ?? null;
    try {
      if (processId && session.loggedIn && !session.isGuest) {
        const selected = await selectResult(processId, selectedUrl, session.userId);
        if (selected.process_id) task.process_id = selected.process_id;
      }
      // 대시보드 작업 업데이트: 선택된 이미지만 남기고 needsSelection 해제
      if (task) {
        editStore.update(s => ({
          ...s,
          dashboardTasks: s.dashboardTasks.map(t =>
            t.id === store.currentTaskId
              ? { ...t, process_id: task.process_id, resultImage: selectedUrl, resultImages: null, needsSelection: false }
              : t
          )
        }));
      }
      // imageStore에서도 resultImages 정리
      imageStore.update(s => ({ ...s, resultImages: null }));
      // 선택 결과를 서버에도 저장하여 대시보드에서도 유지
      if (session.loggedIn && !session.isGuest) {
        const updatedTasks = get(editStore).dashboardTasks;
        saveTasksToServer(session.userId, session.sessionId, updatedTasks).catch(e =>
          console.error('작업 목록 저장 실패:', e)
        );
      }
      gallerySelectionDone = true;
    } catch (e) {
      console.error('갤러리 저장 선택 오류:', e);
      alert('갤러리 저장 중 오류가 발생했습니다.');
    } finally { selecting = false; }
  }
  $: patternBg = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='40'><text x='0' y='20' fill='%23cccccc' fill-opacity='0.2' font-size='20'>${patternText}</text></svg>")`;

  function downloadImage() {
    // resultImage가 있으면 그걸 사용, 없을 때는 serverImageUrl → uploadedImageUrl 순으로 선택
    const imageUrl = $imageStore.resultImage || $imageStore.serverImageUrl || $imageStore.uploadedImageUrl;
    if (!imageUrl) return;
    
    const pathWithoutQuery = imageUrl.split('?')[0].toLowerCase();
    const extension = pathWithoutQuery.endsWith('.mp4') ? 'mp4' : 'png';
    downloadFile(imageUrl, `edited_dog_image_${Date.now()}.${extension}`);
  }

  let lastDialogOpen = 0;
  function openFileDialog() {
    const now = Date.now();
    if (now - lastDialogOpen < 500) return;
    lastDialogOpen = now;
    if (fileInput) fileInput.value = '';
    fileInput.click();
  }

  function handleFileSelect(event) {
    const file = event.target.files[0];
    if (file) {
      validateAndUploadFile(file);
    }
    // 같은 파일을 다시 선택할 수 있도록 입력값 초기화
    event.target.value = '';
  }

  async function validateAndUploadFile(file) {

    if (!file.type.match('image.*')) {
      errorMessage = '이미지 파일만 업로드할 수 있습니다.';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      errorMessage = '파일 크기는 10MB 이하여야 합니다.';
      return;
    }

    errorMessage = '';

    // 압축, 미리보기 디코딩, 서버 업로드는 모두 비동기로 끝난다. 단조 증가 버전으로
    // 이전 선택의 콜백이 최신 이미지를 덮어쓰지 않게 한다.
    const selectionVersion = ++_imageSelectionVersion;

    // 이미지 압축 (새로운 File 객체 생성)
    const compressed = await compressImage(file);
    if (selectionVersion !== _imageSelectionVersion) return;
    // 미리보기용 object URL 생성
    if (_objectUrl) {
      try { URL.revokeObjectURL(_objectUrl); } catch {}
    }
    const objectUrl = URL.createObjectURL(compressed);
    _objectUrl = objectUrl;

    // 이전 작업을 동기적으로 초기화한다. 특히 img.onload에서 serverImageUrl을 나중에
    // 지우면 안 된다. 빠른 네트워크에서는 이미지 디코딩 전에 업로드가 끝나 영구 URL을
    // 잃을 수 있다.
    imageStore.update(store => ({
      ...store,
      uploadedImageUrl: objectUrl,
      uploadedImage: compressed,
      imageWidth: 0,
      imageHeight: 0,
      resultImage: null,
      resultImages: null,
      resultFeature: null,
      resultOptions: null,
      serverImageUrl: null,
      currentTaskImageUrl: null,
      taskOriginalImageUrl: null
    }));
    editStore.update(store => ({
      ...store,
      currentTaskId: null,
      editHistory: [],
      currentEditIndex: -1
    }));

    // 미리보기 및 스토어 업데이트
    const img = new Image();
    img.onload = () => {
      if (selectionVersion !== _imageSelectionVersion || _objectUrl !== objectUrl) return;
      imageStore.update(store => ({
        ...store,
        imageWidth: img.width,
        imageHeight: img.height
      }));
      if (typeof window !== 'undefined' && window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler('imageUploaded');
      }
    };
    img.onerror = async () => {
      if (selectionVersion !== _imageSelectionVersion || _objectUrl !== objectUrl) return;
      // object URL로 로드에 실패하면 Base64 변환 시도
      try {
        const dataUrl = await fileToBase64(compressed);
        if (selectionVersion !== _imageSelectionVersion || _objectUrl !== objectUrl) return;
        imageStore.update(store => ({
          ...store,
          uploadedImageUrl: dataUrl
        }));
        errorMessage = '미리보기를 Base64로 대체했습니다.';
      } catch (e) {
        console.error('Base64 변환 실패', e);
        errorMessage = '이미지를 표시할 수 없습니다.';
      }
    };
    img.src = objectUrl;


  }

  onMount(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  });
</script>

<div class="image-area" style="--pattern-bg: {patternBg};">
  {#if $imageStore.uploadedImageUrl}
    <div class="preview-wrapper">
      <ImagePreview showZoom={false} hideLeftBorder={true} />
    </div>
    <div class="v2-result-actions">
      {#if hasMultipleResults}
        <div class="version-tabs">
          {#each resultImages as _, i}
            <button
              class="version-tab {i === selectedImageIndex ? 'active' : ''}"
              on:click={() => selectResultImage(i)}
            >버전 {i + 1}</button>
          {/each}
        </div>
        {#if gallerySelectionDone}
          <div class="gallery-save-done">✓ 갤러리 저장 완료</div>
        {:else}
          <button disabled={selecting} class="gallery-save-btn" on:click={selectForGallery}>
            이 버전 ({selectedImageIndex + 1}번) 갤러리에 저장
          </button>
        {/if}
      {/if}
      {#if $editStore.selectedFeature && $editStore.selectedFeature.id === 'upload_image'}
        <button class="action-btn change-image-btn" on:click={openFileDialog}>
          <span class="btn-icon">🔄</span>
          <span>이미지 변경</span>
        </button>
        <input
          type="file"
          bind:this={fileInput}
          on:change={handleFileSelect}
          accept="image/*"
          style="display: none;"
        />
      {:else}
        <button class="action-btn download-btn" on:click={downloadImage}>
          <span class="btn-icon">💾</span>
          <span>이미지 다운로드</span>
        </button>
      {/if}
    </div>
    {#if $imageStore.resultImage && !hasMultipleResults}
      <ResultTools image={$imageStore.resultImage} task={resultTask} showDownload={false} />
    {/if}
    {#if errorMessage}
      <div class="error-message">{errorMessage}</div>
    {/if}
  {:else}
    <UploadSection />
  {/if}
</div>

<style>
  .image-area {
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }
  .preview-wrapper {
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  /* 다중 이미지 버전 탭 */
  .version-tabs {
    display: flex;
    gap: 0.3rem;
  }
  .version-tab {
    padding: 0.3rem 0.85rem;
    border-radius: 20px;
    border: 1.5px solid #FF6A00;
    background: transparent;
    color: #FF6A00;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease, transform 0.15s ease;
  }
  .version-tab:hover {
    background: rgba(255,106,0,0.1);
    transform: translateY(-1px);
  }
  .version-tab.active {
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
    border-color: transparent;
    box-shadow: 0 2px 8px rgba(255,106,0,0.35);
  }
  .gallery-save-btn {
    padding: 0.35rem 1rem;
    border-radius: 20px;
    border: 1.5px solid #FF6A00;
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.18s ease, transform 0.15s ease;
    box-shadow: 0 2px 8px rgba(255,106,0,0.35);
  }
  .gallery-save-btn:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }
  .gallery-save-done {
    padding: 0.35rem 1rem;
    border-radius: 20px;
    border: 1.5px solid #27ae60;
    background: linear-gradient(90deg, #43d17a, #27ae60);
    color: white;
    font-size: 0.82rem;
    font-weight: 700;
  }
  .image-area::before {
    content: '';
    position: absolute;
    inset: -40%;
    background-image: var(--pattern-bg);
    background-repeat: repeat;
    transform: rotate(20deg);
    transform-origin: center;
    z-index: -1;
    pointer-events: none;
  }
  .image-area :global(.edit-image),
  .image-area :global(.edit-canvas) {
    max-width: 100%;
    max-height: 57vh;
  }
  .v2-result-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 0.5rem;
  }
  .action-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1.2rem;
    border-radius: 8px;
    border: none;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  .download-btn {
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
  }
  .change-image-btn {
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    color: white;
  }
  .action-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  .btn-icon {
    font-size: 1.2rem;
  }
  .error-message {
    color: #FF6382;
    text-align: center;
    padding: 0.5rem;
  }
  @media (max-width: 800px) { .image-area { padding-bottom: 16px; } }
</style>
